import React, { ReactElement } from 'react'
import cx from 'classnames'
import { Icon, List, l, Popover } from '@manychat/manyui'

import { HelpRefreshPermissionButton } from 'common/core/components/HelpRefreshPermissionButton/HelpRefreshPermissionButton'
import { PrivacyPolicyURL, TosURL } from 'utils/assets'
import { analyticsService } from 'utils/services/analytics'
import { useIsIntercomFinAIEnabled } from 'utils/services/intercomFinAIAgent/useIntercomFinAIAgentWidget'

import { KnowledgeBaseLink } from '../SupportLinks'

import cm from './HelpPopover.module.css'

interface IHelpPopoverProps {
  open: boolean
  anchor?: HTMLElement | ReactElement
  pageId?: string
  useLayerForClickAway?: boolean
  onRequestClose: () => void
}

const HelpPopover = ({
  open,
  anchor,
  pageId,
  useLayerForClickAway = false,
  onRequestClose,
}: IHelpPopoverProps) => {
  const isIntercomFinAIEnabled = useIsIntercomFinAIEnabled()
  const handleRefreshPermissionsClick = () => {
    onRequestClose()
    analyticsService.sendEvent('HELP.REFRESH_PERMISSIONS.CLICK')
  }

  const linkClickHandlers = {
    getHelp: () => analyticsService.sendEvent('HELP.GET_HELP.CLICK'),
    helpCenter: () => analyticsService.sendEvent('HELP.HELP_CENTER.CLICK'),
    changeLog: () => analyticsService.sendEvent('HELP.CHANGELOG.CLICK'),
    joinCommunity: () => analyticsService.sendEvent('HELP.COMMUNITY.CLICK'),
    blog: () => analyticsService.sendEvent('HELP.BLOG.CLICK'),
    hireAnAgency: () => analyticsService.sendEvent('HELP.AGENCY.CLICK'),
    termsOfService: () => analyticsService.sendEvent('HELP.TERMS.CLICK'),
    privacyPolicy: () => analyticsService.sendEvent('HELP.PRIVACY_POLICY.CLICK'),
    privacySettings: async () => {
      await window?.__ucCmp?.showSecondLayer()
      analyticsService.sendEvent('HELP.PRIVACY_SETTINGS.CLICK')
    },
  }

  return (
    <Popover
      open={open && anchor != null}
      anchor={anchor}
      at="right"
      arrow
      theme={{ base: 'm-l-xxs', arrow: 'm-l-xxs' }}
      onRequestClose={onRequestClose}
      useLayerForClickAway={useLayerForClickAway}
      data-test-id="help-popover-content"
    >
      <div className={cx(cm.wrapper, 'rounded')}>
        <List className="b-y">
          {pageId && isIntercomFinAIEnabled && (
            <List.Item
              leftContent={<Icon.Help size={24} />}
              className="fin-ai-trigger"
              onClick={() => {
                linkClickHandlers.getHelp()
              }}
            >
              {l.translate('Get help')}
            </List.Item>
          )}
          <List.Item
            leftContent={<Icon.Book size={24} />}
            onClick={() => {
              linkClickHandlers.helpCenter()
              window.open(KnowledgeBaseLink.LOGIN, '_blank', 'noopener,noreferrer')
            }}
          >
            {l.translate('Knowledge base')}
          </List.Item>
          <List.Item
            leftContent={<Icon.Community size={24} />}
            onClick={() => {
              linkClickHandlers.joinCommunity()
              window.open('https://community.manychat.com/', '_blank', 'noopener,noreferrer')
            }}
          >
            {l.translate('Ask the Community')}
          </List.Item>
          <List.Item
            leftContent={<Icon.TemplateTextMedia size={24} />}
            onClick={() => {
              linkClickHandlers.hireAnAgency()
              window.open(
                'https://www.manychat.com/agency/listing',
                '_blank',
                'noopener,noreferrer',
              )
            }}
          >
            {l.translate('Hire an Agency')}
          </List.Item>
          <List.Item
            leftContent={<Icon.Creator size={24} />}
            onClick={() => {
              linkClickHandlers.joinCommunity()
              window.open(
                'https://community.manychat.com/ideas?sort=votes',
                '_blank',
                'noopener,noreferrer',
              )
            }}
          >
            {l.translate('Share an Idea')}
          </List.Item>
          <List.Item
            leftContent={<Icon.PublicChangelog size={24} />}
            onClick={() => {
              linkClickHandlers.changeLog()
              window.open(
                'https://community.manychat.com/product-updates',
                '_blank',
                'noopener,noreferrer',
              )
            }}
          >
            {l.translate('Manychat Changelog')}
          </List.Item>
          <List.Item
            leftContent={<Icon.Note size={24} />}
            onClick={() => {
              linkClickHandlers.blog()
              window.open('https://manychat.com/blog/', '_blank', 'noopener,noreferrer')
            }}
          >
            {l.translate('Manychat Blog')}
          </List.Item>
        </List>

        <List>
          <List.Item
            onClick={() => {
              linkClickHandlers.termsOfService()
              window.open(TosURL, '_blank', 'noopener,noreferrer')
            }}
          >
            {l.translate('Terms of service')}
          </List.Item>
          <List.Item
            onClick={() => {
              linkClickHandlers.privacyPolicy()
              window.open(PrivacyPolicyURL, '_blank', 'noopener,noreferrer')
            }}
          >
            {l.translate('Privacy policy')}
          </List.Item>
          <List.Item onClick={linkClickHandlers.privacySettings}>
            {l.translate('Privacy settings')}
          </List.Item>
        </List>

        {pageId && <HelpRefreshPermissionButton onClick={handleRefreshPermissionsClick} />}
      </div>
    </Popover>
  )
}

export default HelpPopover
